import classNames from 'classnames';
import * as React from 'react';
import { RefObject } from 'react';

import { Bem } from '../../service/Bem/Bem';
import { KeyCode } from '../../typings/common';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { JsSubmitForm } from '../JsSubmitForm/JsSubmitForm';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';

import './ExpandedEditBlock.css';

export interface ExpandedEditBlockProps {
    header?: React.ReactNode;
    footer?: React.ReactNode;
    cls?: string;
    isOpen: boolean;
    onCloseClick?: () => void;
    onOpenClick?: () => void;
    showOpenControl: boolean;
    showCloseControl: boolean;
    isOpenDisabled?: boolean;
    renderSmallShadowOnOpen?: boolean;
    isMainContainer?: boolean;
    disabled?: boolean;
    iconType?: Shape2Icon.edit | Shape2Icon.verticalArrow | Shape2Icon.close;
}

const editCardControlBlock = 'expanded-edit-block';

export class ExpandedEditBlock extends React.Component<ExpandedEditBlockProps> {
    private headerRef: RefObject<HTMLDivElement> = React.createRef();

    render() {
        const {
            isOpen,
            children,
            cls,
            disabled,
            isMainContainer = false,
            renderSmallShadowOnOpen = false
        } = this.props;

        return (
            <JsSubmitForm
                cls={classNames(
                    cls,
                    Bem.cls({
                        block: editCardControlBlock,
                        mods: {
                            opened: isOpen ? 'yes' : 'no',
                            disabled: Boolean(disabled),
                            'small-shadow': renderSmallShadowOnOpen,
                            main: isMainContainer
                        }
                    })
                )}
                onKeyDown={this.onKeyDown}
            >
                {this.renderHeader()}
                <div className={Bem.cls(editCardControlBlock, 'content')}>{children}</div>
                {this.renderFooter()}
            </JsSubmitForm>
        );
    }

    private renderHeader = () => {
        const { header, isOpen, isOpenDisabled = false, showOpenControl, showCloseControl } = this.props;

        if (!header) {
            return undefined;
        }

        const isShowHeaderControl = (isOpen && showCloseControl) || (!isOpen && showOpenControl);

        const mods = {
            'cursor-disabled': isOpenDisabled || !isShowHeaderControl
        };
        const control = isOpen ? this.getCloseControl() : this.getOpenControl();

        return (
            <div
                className={Bem.cls(editCardControlBlock, 'header', mods)}
                onClick={this.toggleView}
                ref={this.headerRef}
            >
                <div className={Bem.cls(editCardControlBlock, 'title', mods)}>{header}</div>
                {control && (
                    <ButtonDecorator
                        cls={Bem.cls(editCardControlBlock, 'expand-button')}
                        size={'n'}
                        theme={'clear'}
                        onClick={this.toggleClose}
                    >
                        {control}
                    </ButtonDecorator>
                )}
            </div>
        );
    };

    private toggleView = (event: React.MouseEvent<HTMLDivElement>) => {
        const { isOpen, disabled, showOpenControl, showCloseControl } = this.props;

        if (disabled) {
            return;
        }

        let target = event.target as HTMLElement;

        while (target !== this.headerRef.current) {
            if (target.tagName === 'A') {
                return;
            }

            if (target.tagName === 'BODY') {
                return;
            }

            if (target.parentElement !== null) {
                target = target.parentElement;
            }
        }

        if (isOpen && showCloseControl) {
            this.onCloseClick();
        } else if (!isOpen && showOpenControl) {
            this.onOpenClick();
        }
    };

    private renderFooter = () => {
        const { footer } = this.props;

        if (!footer) {
            return undefined;
        }

        return <div className={Bem.cls(editCardControlBlock, 'footer')}>{footer}</div>;
    };

    private getOpenControl = () => {
        const { showOpenControl, disabled, isOpen, iconType = Shape2Icon.edit, isOpenDisabled } = this.props;

        if (!showOpenControl || disabled) {
            return null;
        }

        return (
            <Shape2
                cls={Bem.cls({
                    block: editCardControlBlock,
                    elem: 'control-element',
                    mods: {
                        iconType: iconType,
                        opened: isOpen ? 'yes' : 'no'
                    }
                })}
                hoverable={true}
                theme={Shape2Theme.light}
                icon={iconType}
                disabled={isOpenDisabled}
                width={14}
                height={14}
            />
        );
    };

    private getCloseControl = () => {
        const { showCloseControl, isOpen, iconType = Shape2Icon.close, isOpenDisabled } = this.props;

        if (!showCloseControl) {
            return null;
        }

        return (
            <Shape2
                cls={Bem.cls({
                    block: editCardControlBlock,
                    elem: 'control-element',
                    mods: {
                        iconType: iconType,
                        opened: isOpen ? 'yes' : 'no'
                    }
                })}
                hoverable={true}
                theme={Shape2Theme.light}
                icon={iconType}
                disabled={isOpenDisabled}
                width={14}
                height={14}
            />
        );
    };

    private onOpenClick = () => {
        const { isOpenDisabled, onOpenClick } = this.props;

        if (!isOpenDisabled && onOpenClick) {
            onOpenClick();
        }
    };

    private onCloseClick = () => {
        const { onCloseClick } = this.props;

        if (onCloseClick) {
            onCloseClick();
        }
    };

    private toggleClose = () => {
        if (this.props.isOpen) {
            this.onCloseClick();
        } else {
            this.onOpenClick();
        }
    };

    private onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
        const code = e.keyCode;

        if (code === KeyCode.ESC) {
            if (this.props.onCloseClick) {
                this.props.onCloseClick();
            }
        }
    };
}
